import { useIntl } from 'react-intl';

import { DataFieldOperators } from '~/enums/DataFieldOperators';
import { DataFieldType } from '~/enums/DataFieldType';

export const useFilterFieldOptions = () => {
  const intl = useIntl();

  const BLANK_OPTIONS = [
    {
      value: DataFieldOperators.IsBlank,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.isBlank',
        defaultMessage: 'is blank',
        description: 'Filter option for blank values',
      }),
    },
    {
      value: DataFieldOperators.IsNotBlank,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.isNotBlank',
        defaultMessage: 'is not blank',
        description: 'Filter option for non-blank values',
      }),
    },
  ];

  const NUMBER_OPTIONS = [
    {
      value: DataFieldOperators.LessThan,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.lessThan',
        defaultMessage: 'less than',
        description: 'Number comparison option',
      }),
    },
    {
      value: DataFieldOperators.LessThanOrEqualsTo,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.lessThanOrEqualsTo',
        defaultMessage: 'less than or equals to',
        description: 'Number comparison option',
      }),
    },
    {
      value: DataFieldOperators.Exactly,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.exactly',
        defaultMessage: 'exactly',
        description: 'Number comparison option',
      }),
    },
    {
      value: DataFieldOperators.GreaterThanOrEqualsTo,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.greaterThanOrEqualsTo',
        defaultMessage: 'greater than or equal to',
        description: 'Number comparison option',
      }),
    },
    {
      value: DataFieldOperators.GreaterThan,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.greaterThan',
        defaultMessage: 'greater than',
        description: 'Number comparison option',
      }),
    },
    {
      value: DataFieldOperators.Between,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.between',
        defaultMessage: 'between',
        description: 'Number comparison option',
      }),
    },
    ...BLANK_OPTIONS,
  ];

  const STRING_OPTIONS = [
    {
      value: DataFieldOperators.ContainsText,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.containsText',
        defaultMessage: 'contains text',
        description: 'String filter option',
      }),
    },
    {
      value: DataFieldOperators.DoesNotContainText,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.doesNotContainText',
        defaultMessage: 'does not contain text',
        description: 'String filter option',
      }),
    },
    ...BLANK_OPTIONS,
  ];

  const DATE_OPTIONS = [
    {
      value: DataFieldOperators.DateBefore,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.beforeDate',
        defaultMessage: 'before date',
        description: 'Date filter option',
      }),
    },
    {
      value: DataFieldOperators.DateEquals,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.onDate',
        defaultMessage: 'on date',
        description: 'Date filter option',
      }),
    },
    {
      value: DataFieldOperators.DateAfter,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.afterDate',
        defaultMessage: 'after date',
        description: 'Date filter option',
      }),
    },
    {
      value: DataFieldOperators.DateBetween,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.betweenDates',
        defaultMessage: 'between dates',
        description: 'Date filter option',
      }),
    },
    {
      value: DataFieldOperators.Next,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.inTheNext',
        defaultMessage: 'in the next',
        description: 'Date filter option',
      }),
    },
    {
      value: DataFieldOperators.Last,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.inTheLast',
        defaultMessage: 'in the last',
        description: 'Date filter option',
      }),
    },
    ...BLANK_OPTIONS,
  ];

  const ARRAY_SINGLE_OPTIONS = [
    {
      value: DataFieldOperators.Equals,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.equals',
        defaultMessage: 'equals',
        description: 'Array single filter option',
      }),
    },
    {
      value: DataFieldOperators.DoesNotEqual,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.doesNotEqual',
        defaultMessage: 'does not equal',
        description: 'Array single filter option',
      }),
    },
    ...BLANK_OPTIONS,
  ];

  const ARRAY_MULTIPLE_OPTIONS = [
    {
      value: DataFieldOperators.ContainsAll,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.containsAll',
        defaultMessage: 'contains all',
        description: 'Array multiple filter option',
      }),
    },
    {
      value: DataFieldOperators.ContainsAny,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.containsAny',
        defaultMessage: 'contains any',
        description: 'Array multiple filter option',
      }),
    },
    {
      value: DataFieldOperators.DoesNotContain,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.doesNotContain',
        defaultMessage: 'does not contain',
        description: 'Array multiple filter option',
      }),
    },
    ...BLANK_OPTIONS,
  ];

  const ATTACHMENT_OPTIONS = [
    {
      value: DataFieldOperators.IsNotBlank,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.hasFilesAttached',
        defaultMessage: 'Has Files Attached',
        description: 'Attachment filter option',
      }),
    },
    {
      value: DataFieldOperators.IsBlank,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.noFilesAttached',
        defaultMessage: 'No Files Attached',
        description: 'Attachment filter option',
      }),
    },
  ];

  const DOCUMENT_ID_OPTIONS = [
    {
      value: DataFieldOperators.ContainsAny,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.containsAnyDocId',
        defaultMessage: 'contains any',
        description: 'Document ID filter option',
      }),
    },
    {
      value: DataFieldOperators.DoesNotContain,
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.doesNotContainDocId',
        defaultMessage: 'does not contain',
        description: 'Document ID filter option',
      }),
    },
  ];

  const BOOLEAN_OPTIONS = [
    {
      value: 'true',
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.true',
        defaultMessage: 'True',
        description: 'Boolean true option',
      }),
    },
    {
      value: 'false',
      label: intl.formatMessage({
        id: 'useFilterFieldOptions.false',
        defaultMessage: 'False',
        description: 'Boolean false option',
      }),
    },
    ...BLANK_OPTIONS,
  ];

  function getRelationOptions(type: DataFieldType) {
    switch (type) {
      case DataFieldType.NUMBER:
        return NUMBER_OPTIONS;
      case DataFieldType.STRING:
      case DataFieldType.TEXT_AREA:
        return STRING_OPTIONS;
      case DataFieldType.DATE:
        return DATE_OPTIONS;
      case DataFieldType.ARRAY_SINGLE:
        return ARRAY_SINGLE_OPTIONS;
      case DataFieldType.ARRAY_MULTIPLE:
        return ARRAY_MULTIPLE_OPTIONS;
      default:
        return null;
    }
  }

  return {
    getRelationOptions,
    ATTACHMENT_OPTIONS,
    DOCUMENT_ID_OPTIONS,
    BOOLEAN_OPTIONS,
  };
};
